import React, { forwardRef, ButtonHTMLAttributes, ReactNode, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { trackButtonClick } from '../../lib/analytics';
import { trackButtonClick as trackFirebaseButtonClick } from '../../firebaseConfig';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger';
  children: ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  // Analytics props
  trackingId?: string;
  trackingLocation?: string;
  trackingDisabled?: boolean;
  trackingParams?: Record<string, any>;
  analyticsName?: string;
  analyticsLocation?: string;
  analyticsMetadata?: Record<string, any>;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  children,
  leftIcon,
  rightIcon,
  fullWidth = false,
  size = 'md',
  loading = false,
  disabled = false,
  className = '',
  // Analytics props
  trackingId,
  trackingLocation,
  trackingDisabled = false,
  trackingParams = {},
  analyticsName,
  analyticsLocation,
  analyticsMetadata,
  onClick,
  ...rest
}, ref) => {
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };
  
  const baseClasses = 'relative overflow-hidden !font-heading rounded-full transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 isolate group';
  
  const variantClasses = {
    primary: `
      btn-primary !font-heading
      bg-primary-light hover:bg-primary-hover-light text-white 
      shadow-button-light dark:bg-primary-dark dark:hover:bg-primary-hover-dark 
      dark:shadow-button-dark
      before:content-[''] before:absolute before:inset-0 before:rounded-full
      before:bg-gradient-to-r before:from-primary-light before:via-secondary-light before:to-primary-light
      dark:before:from-primary-dark dark:before:via-secondary-dark dark:to-primary-dark
      before:opacity-0 hover:before:opacity-10 before:transition-opacity before:duration-300 before:-z-10
    `,
    secondary: `
      btn-secondary !font-heading
      bg-surface-light dark:bg-background-dark/50 backdrop-blur-md
      border-2 border-border-light dark:border-border-dark
      text-text-primary-light dark:text-text-primary-dark
      shadow-sm
      hover:bg-primary-light/5 dark:hover:bg-primary-dark/5
      hover:border-primary-light dark:hover:border-primary-dark
      before:content-[''] before:absolute before:inset-0 before:rounded-full
      before:bg-gradient-to-r before:from-primary-light before:via-secondary-light before:to-primary-light
      dark:before:from-primary-dark dark:before:via-secondary-dark dark:to-primary-dark
      before:opacity-0 hover:before:opacity-5 before:transition-opacity before:duration-300 before:-z-10
    `,
    outline: `
      btn-outline !font-heading
      bg-transparent
      border border-border-light dark:border-border-dark
      text-text-secondary-light dark:text-text-secondary-dark
      hover:border-primary-light dark:hover:border-primary-dark
      hover:text-primary-light dark:hover:text-primary-dark
      transition-colors duration-300
    `,
    danger: `
      btn-danger !font-heading
      bg-red-500 hover:bg-red-600 text-white 
      shadow-button-light dark:bg-red-600 dark:hover:bg-red-700 
      dark:shadow-button-dark
      before:content-[''] before:absolute before:inset-0 before:rounded-full
      before:bg-gradient-to-r before:from-red-500 before:via-red-400 before:to-red-500
      dark:before:from-red-600 dark:before:via-red-500 dark:before:to-red-600
      before:opacity-0 hover:before:opacity-10 before:transition-opacity before:duration-300 before:-z-10
    `
  };

  const isDisabled = loading || disabled;
  
  // Handle click with analytics tracking
  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    // Track button click if analytics props are provided
    if (analyticsName && analyticsLocation) {
      // Track with unified analytics (Firebase, Meta Pixel, GA4, Clarity)
      trackButtonClick(analyticsName, analyticsLocation, {
        button_variant: variant,
        button_size: size,
        button_text: typeof children === 'string' ? children : 'Complex Content',
        is_loading: loading,
        is_disabled: disabled,
        is_full_width: fullWidth,
        ...analyticsMetadata
      });

      // Keep Firebase analytics for backward compatibility
      trackFirebaseButtonClick(analyticsName, analyticsLocation, {
        button_variant: variant,
        button_size: size,
        button_text: typeof children === 'string' ? children : 'Complex Content',
        is_loading: loading,
        is_disabled: disabled,
        is_full_width: fullWidth,
        ...analyticsMetadata
      });
    }
    
    // Track the button click with enhanced analytics
    if (!isDisabled && !trackingDisabled && trackingId) {
      // Use provided tracking ID or fallback to button text content
      const buttonName = trackingId || (typeof children === 'string' ? children : 'Button');
      // Use provided location or fallback to document title
      const buttonLocation = trackingLocation || document.title || 'Unknown';
      
      trackButtonClick(buttonName, buttonLocation, {
        button_variant: variant,
        button_size: size,
        button_text: typeof children === 'string' ? children : undefined,
        ...trackingParams
      });
    }
    
    // Call the original onClick handler if provided
    if (onClick) {
      onClick(e);
    }
  }, [children, isDisabled, onClick, trackingDisabled, trackingId, trackingLocation, trackingParams, variant, size, loading, disabled, fullWidth, analyticsMetadata, analyticsName, analyticsLocation]);
  
  return (
    <button
      ref={ref}
      className={`
        ${baseClasses}
        ${variantClasses[variant]} 
        ${sizeClasses[size]} 
        ${fullWidth ? 'w-full' : ''} 
        ${isDisabled ? 'cursor-not-allowed opacity-70' : ''}
        !font-heading
        ${className}
      `}
      disabled={isDisabled}
      onClick={handleClick}
      {...rest}
    >
      <span className="absolute inset-0 rounded-full overflow-hidden">
        <span className="absolute inset-0 w-[100%] bg-gradient-to-r from-transparent via-white to-transparent opacity-0 -translate-x-full group-hover:translate-x-full group-hover:opacity-20 transition-all duration-1000 ease-out"></span>
      </span>
      
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
      ) : leftIcon && (
        <span className="inline-flex">{leftIcon}</span>
      )}
      <span className="relative z-10 !font-heading">{children}</span>
      {!loading && rightIcon && <span className="inline-flex">{rightIcon}</span>}
    </button>
  );
});

Button.displayName = 'Button'; 